import { useEffect, useRef, useState } from "react";
import { APIProvider, Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_BROWSER_KEY;

export function buildNavUrl(a) {
  if (!a) return "#";
  const enc = encodeURIComponent;
  const origin = a.origin ? `&origin=${enc(a.origin)}` : "";
  const wp = a.patient ? `&waypoints=${enc(a.patient)}` : "";
  const dest = enc(a.clinic || a.patient || a.origin || "");
  return `https://www.google.com/maps/dir/?api=1${origin}${wp}&destination=${dest}&travelmode=driving`;
}

function Overlay({ points, polyline, driverLocation, traffic }) {
  const map = useMap();
  const geometry = useMapsLibrary("geometry");
  const objsRef = useRef([]);
  const driverRef = useRef(null);
  const trafficRef = useRef(null);

  useEffect(() => {
    if (!map || !window.google || !points) return;
    const g = window.google.maps;
    objsRef.current.forEach((o) => o.setMap(null));
    objsRef.current = [];
    const bounds = new g.LatLngBounds();
    const mk = (pt, color, label) => {
      if (!pt) return;
      const pos = { lat: pt[0], lng: pt[1] };
      const m = new g.Marker({
        position: pos, map, label: { text: label, color: "#fff", fontWeight: "700", fontSize: "12px" },
        icon: { path: g.SymbolPath.CIRCLE, scale: 12, fillColor: color, fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 },
      });
      objsRef.current.push(m); bounds.extend(pos);
    };
    mk(points.origin, "#27b35f", "O");
    mk(points.patient, "#f0a52b", "P");
    mk(points.clinic, "#e54545", "C");
    let path = null;
    if (polyline && geometry) { try { path = geometry.encoding.decodePath(polyline); } catch { path = null; } }
    if (!path) path = [points.origin, points.patient, points.clinic].filter(Boolean).map((p) => ({ lat: p[0], lng: p[1] }));
    if (path && path.length) {
      const line = new g.Polyline({ path, map, strokeColor: "#1671d9", strokeWeight: 5, strokeOpacity: 0.9 });
      objsRef.current.push(line);
      path.forEach((p) => bounds.extend(p));
    }
    if (!bounds.isEmpty()) map.fitBounds(bounds, 60);
    return () => { objsRef.current.forEach((o) => o.setMap(null)); objsRef.current = []; };
  }, [map, geometry, points, polyline]);

  useEffect(() => {
    if (!map || !window.google || !driverLocation) return;
    const g = window.google.maps;
    const pos = { lat: driverLocation.lat, lng: driverLocation.lng };
    if (!driverRef.current) {
      driverRef.current = new g.Marker({
        position: pos, map, zIndex: 999, title: "Motorista",
        icon: { path: g.SymbolPath.FORWARD_CLOSED_ARROW, scale: 6, fillColor: "#0d4478", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2, rotation: driverLocation.heading || 0 },
      });
    } else {
      driverRef.current.setPosition(pos);
      const icon = driverRef.current.getIcon();
      if (icon) driverRef.current.setIcon({ ...icon, rotation: driverLocation.heading || 0 });
    }
  }, [map, driverLocation]);

  useEffect(() => {
    if (!map || !window.google) return;
    if (traffic && !trafficRef.current) { trafficRef.current = new window.google.maps.TrafficLayer(); trafficRef.current.setMap(map); }
    if (!traffic && trafficRef.current) { trafficRef.current.setMap(null); trafficRef.current = null; }
  }, [map, traffic]);

  return null;
}

export default function RouteMap({ points, polyline, driverLocation, height = 320 }) {
  const [traffic, setTraffic] = useState(false);
  if (!KEY) return <div style={{ padding: 24, textAlign: "center", color: "#6d8094" }}>Mapa indisponível (chave do Google não configurada).</div>;
  const center = points?.patient ? { lat: points.patient[0], lng: points.patient[1] }
    : points?.origin ? { lat: points.origin[0], lng: points.origin[1] } : { lat: -23.55, lng: -46.63 };
  return (
    <div style={{ position: "relative", width: "100%", height }}>
      <APIProvider apiKey={KEY} libraries={["geometry"]}>
        <Map style={{ width: "100%", height: "100%" }} defaultCenter={center} defaultZoom={12}
          gestureHandling="greedy" mapTypeControl={true} zoomControl={true} streetViewControl={false}
          fullscreenControl={true} clickableIcons={false} data-testid="google-route-map">
          <Overlay points={points} polyline={polyline} driverLocation={driverLocation} traffic={traffic} />
        </Map>
      </APIProvider>
      <button type="button" className="map-traffic-btn" onClick={() => setTraffic((t) => !t)} data-testid="map-traffic-toggle">
        {traffic ? "Trânsito: ligado" : "Trânsito"}
      </button>
    </div>
  );
}

function FleetOverlay({ units, focusId }) {
  const map = useMap();
  const markersRef = useRef({});
  useEffect(() => {
    if (!map || !window.google) return;
    const g = window.google.maps;
    const bounds = new g.LatLngBounds();
    const seen = new Set();
    (units || []).forEach((u, i) => {
      if (!u.location) return;
      const pos = { lat: u.location.lat, lng: u.location.lng };
      seen.add(u.transportId);
      let m = markersRef.current[u.transportId];
      if (!m) { m = new g.Marker({ map }); markersRef.current[u.transportId] = m; }
      const stale = !!u.stale;
      m.setPosition(pos);
      m.setIcon({ path: g.SymbolPath.FORWARD_CLOSED_ARROW, scale: 6.5, rotation: (u.location.heading || 0),
        fillColor: stale ? "#e54545" : "#1671d9", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2,
        labelOrigin: new g.Point(0, 3.2) });
      m.setLabel({ text: String(i + 1), color: stale ? "#b91c1c" : "#0d4478", fontWeight: "700", fontSize: "12px" });
      m.setTitle((u.label || "Ambulância") + (stale ? " — sem sinal de GPS" : ""));
      bounds.extend(pos);
    });
    Object.keys(markersRef.current).forEach((id) => { if (!seen.has(id)) { markersRef.current[id].setMap(null); delete markersRef.current[id]; } });
    if (!bounds.isEmpty() && !focusId) map.fitBounds(bounds, 70);
  }, [map, units, focusId]);
  useEffect(() => {
    if (!map || !focusId) return;
    const u = (units || []).find((x) => x.transportId === focusId);
    if (u?.location) { map.panTo({ lat: u.location.lat, lng: u.location.lng }); map.setZoom(15); }
  }, [map, focusId, units]);
  return null;
}

export function FleetMap({ units, focusId, height = 520 }) {
  if (!KEY) return <div style={{ padding: 24, textAlign: "center", color: "#6d8094" }}>Mapa indisponível (chave do Google não configurada).</div>;
  const first = (units || []).find((u) => u.location);
  const center = first ? { lat: first.location.lat, lng: first.location.lng } : { lat: -23.55, lng: -46.63 };
  return (
    <APIProvider apiKey={KEY}>
      <Map style={{ width: "100%", height }} defaultCenter={center} defaultZoom={11} gestureHandling="greedy"
        mapTypeControl={true} zoomControl={true} streetViewControl={false} fullscreenControl={true} clickableIcons={false} data-testid="fleet-map">
        <FleetOverlay units={units} focusId={focusId} />
      </Map>
    </APIProvider>
  );
}
